import React from "react";
import { motion } from "framer-motion";
import { AlertTriangle, CheckCircle2, TrendingUp, ShieldCheck } from "lucide-react";

const solutions = [
  {
    icon: CheckCircle2,
    title: "Objective verification",
    desc: "AI-driven analysis provides an independent estimate of graft count — free from subjective assessment or manual counting.",
  },
  {
    icon: TrendingUp,
    title: "Documented outcomes",
    desc: "Structured reports create a verifiable record of what was observed post-operation, shareable and archiveable.",
  },
  {
    icon: ShieldCheck,
    title: "Quality differentiation for clinics",
    desc: "Clinics that adopt verification tools can demonstrate their commitment to transparency and quality control.",
  },
];

const WhyItMatters = () => {
  return (
    <section id="why" className="relative py-24 sm:py-32 bg-[#F7F9FC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-2 lg:gap-20 lg:items-start">
          {/* Left: The Problem */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium tracking-[0.1em] uppercase mb-6">
              The Problem
            </div>
            <h2
              className="text-slate-900 font-semibold tracking-tight leading-tight mb-6"
              style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)" }}
            >
              Hair transplant outcomes are difficult to verify
            </h2>
            <div className="space-y-4 text-slate-600 text-base leading-relaxed">
              <p>
                Patients are typically told a graft count before surgery — but
                verifying whether that count was actually delivered has
                historically been impossible without objective measurement tools.
              </p>
              <p>
                Clinics operate without standardized post-operative
                documentation. There is no industry-wide practice for recording
                and reporting graft distribution and density after a procedure.
              </p>
              <p>
                This gap creates a lack of accountability, reduces trust, and
                leaves patients without the documentation they need to assess
                their outcomes.
              </p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="mt-8 flex items-start gap-3 p-4 bg-amber-50 border border-amber-100 rounded-xl"
            >
              <AlertTriangle className="w-5 h-5 text-amber-600 stroke-[1.5] mt-0.5 flex-shrink-0" />
              <p className="text-amber-800 text-sm leading-relaxed">
                Without a structured verification process, there is no reliable
                way for patients to confirm that the promised graft count was
                implanted as described.
              </p>
            </motion.div>
          </motion.div>

          {/* Right: The Solution */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-14 lg:mt-0"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium tracking-[0.1em] uppercase mb-6">
              The Solution
            </div>
            <h2
              className="text-slate-900 font-semibold tracking-tight leading-tight mb-6"
              style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)" }}
            >
              Technology-backed transparency for a trust-first industry
            </h2>

            <div className="space-y-5">
              {solutions.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
                  className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-200/80 hover:border-emerald-200 transition-colors duration-300"
                >
                  <div className="w-9 h-9 bg-emerald-50 rounded-xl border border-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <item.icon className="w-4 h-4 text-emerald-600 stroke-[1.8]" />
                  </div>
                  <div>
                    <h4 className="text-slate-900 font-semibold text-sm mb-1">
                      {item.title}
                    </h4>
                    <p className="text-slate-500 text-sm leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyItMatters;
